import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../../../convex/_generated/api';
import { Id } from '../../../../../convex/_generated/dataModel';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OverviewSection } from '@/components/director/player-detail/overview-section';
import { EvaluationsSection } from '@/components/director/player-detail/evaluations-section';
import { CoachesSection } from '@/components/director/player-detail/coaches-section';
import { ParentsSection } from '@/components/director/player-detail/parents-section';
import { InviteParentSection } from '@/components/director/player-detail/invite-parent-section';
import { EnrollmentsSection } from '@/components/director/player-detail/enrollments-section';

export default function DirectorPlayerDetailPage() {
  const navigate = useNavigate();
  const { playerId } = useParams<{ playerId: string }>();

  if (!playerId) {
    return <div>Invalid player ID</div>;
  }

  const data = useQuery(
    api.director.players.getDetail,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  const isLoading = data === undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center">
        <p className="text-red-500">Failed to load player details</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  const { player, coaches, parents, stats } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Player Info Card */}
      <OverviewSection player={player} stats={stats} />

      {/* Evaluation Averages */}
      {stats.evalAverages && (
        <EvaluationsSection evalAverages={stats.evalAverages} />
      )}

      {/* Invite Parent */}
      <InviteParentSection playerId={playerId as Id<"players">} />

      {/* Enrollments */}
      <EnrollmentsSection playerId={playerId as Id<"players">} />

      {/* Coaches & Parents */}
      <div className="grid md:grid-cols-2 gap-6">
        <CoachesSection coaches={coaches} />
        <ParentsSection parents={parents} />
      </div>
    </div>
  );
}
